import {
  Button,
  Card,
  Checkbox,
  Flex,
  Grid,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useField, useFormikContext } from "formik";
import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { Question } from "src/components/Inputs/QuestionDynamicInputs/Question";
import { CustomQuestionAnswer } from "src/schemas/CustomQuestionType";
import { answerIsEmpty } from "src/services/formTemplate/answer";
import * as AF from "src/types/formTemplate";
import {
  formatCustomQuestionTypeFieldAsProperties,
  formatEmptyInitialValues,
} from "./form/helpers";
import { IDENTIFYING_CONTACT_FIELDS } from "./helpers";

type AdminCustomQuestionProps = {
  formId: uuid;
  customQuestionId: uuid;
  nestedQuestions: AF.ClonedQuestion<AF.WithId>[];
  isRequired: boolean | undefined;
  isDisabled: boolean | undefined;
  customQuestionTypeName: string;
};

type FormValues = {
  [key: string]: CustomQuestionAnswer;
};

export const AdminCustomQuestion: FunctionComponent<
  AdminCustomQuestionProps
> = (props) => {
  const {
    customQuestionId,
    formId,
    nestedQuestions,
    isRequired,
    isDisabled,
    customQuestionTypeName,
  } = props;

  const [{ value }, meta, { setValue, setTouched }] =
    useField<CustomQuestionAnswer>(customQuestionId);

  const [hasAnswer, setHasAnswer] = useState<boolean>(
    !answerIsEmpty(value.answersByQuestionId)
  );

  const hasInitialAnswer = useMemo(
    () => !answerIsEmpty(meta.initialValue?.answersByQuestionId ?? {}),
    [meta.initialValue?.answersByQuestionId]
  );

  const [isNewAnswer, setIsNewAnswer] = useState<boolean>(!hasInitialAnswer);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const withPrefix = useCallback(
    (fieldId: string) => {
      return [customQuestionId, "answersByQuestionId", fieldId].join(".");
    },
    [customQuestionId]
  );

  const nestedQuestionsFieldProps = useMemo(() => {
    return formatCustomQuestionTypeFieldAsProperties({
      applicant: { first_name: null, birth_date: null },
      fieldQuestions: nestedQuestions,
      formId,
      disableDocumentLibrary: true,
    });
  }, [nestedQuestions, formId]);

  const fieldQuestions = useMemo(
    () =>
      nestedQuestionsFieldProps.map((fieldProps) => {
        return (
          <Question
            key={fieldProps.id}
            {...fieldProps}
            id={withPrefix(fieldProps.id)}
            isDisabled={
              (!isRequired && !hasAnswer) || // disable if not required and "Do not answer" is selected
              isDisabled ||
              (!isNewAnswer &&
                IDENTIFYING_CONTACT_FIELDS.includes(fieldProps.key ?? "")) // disable editing identifying contact fields of an existing answer bank entry
            }
          />
        );
      }),
    [
      nestedQuestionsFieldProps,
      isRequired,
      hasAnswer,
      isDisabled,
      isNewAnswer,
      withPrefix,
    ]
  );

  const clearAnswer = useCallback(() => {
    setValue({
      ...value,
      answersByQuestionId: formatEmptyInitialValues(
        nestedQuestions.map((nestedQuestion) => nestedQuestion.id)
      ),
      answerBankId: undefined,
      referenceId: undefined,
    });
  }, [nestedQuestions, setValue, value]);

  const restoreInitialAnswer = useCallback(() => {
    if (meta.initialValue) {
      setValue({
        ...value,
        answersByQuestionId: meta.initialValue.answersByQuestionId,
        answerBankId: meta.initialValue.answerBankId,
        referenceId: meta.initialValue.referenceId,
      });
    }
  }, [meta.initialValue, setValue, value]);

  const toggleHasAnswer = useCallback(() => {
    if (hasAnswer) {
      setHasAnswer(false);
      clearAnswer();
    } else {
      setHasAnswer(true);
      setIsNewAnswer(!hasInitialAnswer);
      restoreInitialAnswer();
    }
    setTouched(true);
  }, [
    hasAnswer,
    setTouched,
    clearAnswer,
    hasInitialAnswer,
    restoreInitialAnswer,
  ]);

  const { errors, touched } = useFormikContext<FormValues>();
  const hasErrors = useMemo(() => {
    const nestedFieldErrors = nestedQuestions.some((question) => {
      const fieldPath = withPrefix(question.id);
      return touched[fieldPath] && errors[fieldPath];
    });
    return nestedFieldErrors || !!meta.error;
  }, [errors, touched, meta.error, nestedQuestions, withPrefix]);

  return (
    <Flex direction="column" gap={2}>
      {!isRequired && (
        <Checkbox
          isChecked={!hasAnswer}
          onChange={toggleHasAnswer}
          isInvalid={false}
          mb={2}
        >
          Do not answer
        </Checkbox>
      )}
      <Flex direction="row" gap={2} alignItems="center">
        <Text
          fontSize="sm"
          color="gray.500"
          fontWeight="400"
          textTransform="uppercase"
        >
          External Ref. ID:
        </Text>
        <Input
          value={value.referenceId ?? ""}
          onChange={(e) => {
            setValue({
              ...value,
              referenceId: e.target.value,
            });
            setTouched(true);
          }}
          placeholder="No external Ref. ID"
          isDisabled={!hasAnswer}
          isInvalid={false} // Don't inherit error state from the rest of CQT answer
          flex={1}
        />
      </Flex>
      {!isOpen && (
        <Card padding={4} gap={4} border="2px solid white">
          <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
            {fieldQuestions}
          </Grid>
        </Card>
      )}
      {hasAnswer && !isNewAnswer && (
        <Button
          size="sm"
          variant="outline"
          colorScheme="gray"
          width="100%"
          _hover={{
            textDecoration: "none",
            backgroundColor: "gray.100",
          }}
          onClick={() => {
            onOpen();
            clearAnswer();
            setIsNewAnswer(true);
          }}
        >
          Add new {customQuestionTypeName.toLocaleLowerCase()}
        </Button>
      )}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text>Add new {customQuestionTypeName.toLocaleLowerCase()}</Text>
          </ModalHeader>
          <ModalBody>{fieldQuestions}</ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              onClick={() => {
                onClose();
                setIsNewAnswer(false);
                restoreInitialAnswer();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                onClose();
                setTouched(true);
              }}
              isDisabled={hasErrors}
            >
              Save {customQuestionTypeName.toLocaleLowerCase()}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};
